import axios from 'axios';
import TaskSystemLogger from './TaskSystemLogger';

/**
 * Setup Axios interceptors for Task System logging
 */
export function setupTaskSystemLogging() {
    // Request interceptor
    axios.interceptors.request.use(
        (config) => {
            const startTime = Date.now();
            config.metadata = { startTime };

            // Only log task system related requests
            const isTaskSystemRoute = config.url && (
                config.url.includes('/task/') || 
                config.url.includes('/projects') || 
                config.url.includes('/kanban')
            );
            
            // Skip logging for the logs endpoint itself to prevent recursion
            if (config.url && config.url.includes('/api/task/logs')) {
                return config;
            }

            // Only log if it's a task system route
            if (isTaskSystemRoute) {
                TaskSystemLogger.logApiRequest(
                    config.method.toUpperCase(),
                    config.url,
                    {
                        params: config.params,
                        data: config.data,
                        headers: config.headers
                    }
                );
            }

            return config;
        },
        (error) => {
            TaskSystemLogger.error('API_REQUEST_ERROR', {
                message: error.message,
                config: error.config
            });
            return Promise.reject(error);
        }
    );

    // Response interceptor
    axios.interceptors.response.use(
        (response) => {
            const duration = Date.now() - (response.config.metadata?.startTime || Date.now());
            
            // Only log task system related responses
            const isTaskSystemRoute = response.config.url && (
                response.config.url.includes('/task/') || 
                response.config.url.includes('/projects') || 
                response.config.url.includes('/kanban')
            );
            
            // Skip logging for the logs endpoint itself
            if (response.config.url && response.config.url.includes('/api/task/logs')) {
                return response;
            }
            
            // Only log if it's a task system route
            if (isTaskSystemRoute) {
                TaskSystemLogger.logApiResponse(
                    response.config.method.toUpperCase(),
                    response.config.url,
                    response.status,
                    response.data,
                    duration
                );
            }

            // Add request ID to response if available
            const requestId = response.headers['x-request-id'];
            if (requestId) {
                response.requestId = requestId;
            }

            return response;
        },
        (error) => {
            const duration = Date.now() - (error.config?.metadata?.startTime || Date.now());
            
            // Only log task system related errors
            const isTaskSystemRoute = error.config?.url && (
                error.config.url.includes('/task/') || 
                error.config.url.includes('/projects') || 
                error.config.url.includes('/kanban')
            );
            
            // Skip logging for the logs endpoint itself
            if (error.config?.url && error.config.url.includes('/api/task/logs')) {
                return Promise.reject(error);
            }
            
            // Only log if it's a task system route
            if (isTaskSystemRoute) {
                // Log error response
                if (error.response) {
                    TaskSystemLogger.logApiResponse(
                        error.config.method.toUpperCase(),
                        error.config.url,
                        error.response.status,
                        error.response.data,
                        duration
                    );
                } else if (error.request) {
                    TaskSystemLogger.error('API_NO_RESPONSE', {
                        method: error.config?.method,
                        url: error.config?.url,
                        message: 'No response received from server',
                        duration
                    });
                } else {
                    TaskSystemLogger.error('API_REQUEST_SETUP_ERROR', {
                        message: error.message,
                        config: error.config
                    });
                }
            }

            return Promise.reject(error);
        }
    );
}

/**
 * Log specific task system operations
 */
export const TaskOperationLogger = {
    // Task operations
    taskCreated(taskData) {
        TaskSystemLogger.logUserAction('TASK_CREATED', {
            taskId: taskData.id,
            title: taskData.title,
            stepId: taskData.step_id,
            projectId: taskData.project_id
        });
    },

    taskUpdated(taskId, changes) {
        TaskSystemLogger.logUserAction('TASK_UPDATED', {
            taskId,
            changes: Object.keys(changes),
            updatedFields: changes
        });
    },

    taskDeleted(taskId) {
        TaskSystemLogger.logUserAction('TASK_DELETED', { taskId });
    },

    taskMoved(taskId, fromStep, toStep) {
        TaskSystemLogger.logUserAction('TASK_MOVED', {
            taskId,
            fromStepId: fromStep,
            toStepId: toStep
        });
    },

    // Comment operations
    commentAdded(taskId, commentData) {
        TaskSystemLogger.logUserAction('COMMENT_ADDED', {
            taskId,
            commentId: commentData.id,
            hasAttachments: commentData.files?.length > 0,
            mentions: commentData.mentions?.length || 0
        });
    },

    // File operations
    fileUploaded(taskId, fileData) {
        TaskSystemLogger.logUserAction('FILE_UPLOADED', {
            taskId,
            fileId: fileData.id,
            fileName: fileData.name,
            fileSize: fileData.size,
            mimeType: fileData.mime
        });
    },

    // View operations
    taskViewed(taskId) {
        TaskSystemLogger.logUserAction('TASK_VIEWED', { taskId });
    },

    boardViewed(projectId) {
        TaskSystemLogger.logUserAction('BOARD_VIEWED', { projectId });
    },

    // Performance logging
    logRenderTime(component, duration) {
        TaskSystemLogger.logPerformance(`${component}_RENDER`, duration);
    },

    // Error logging
    logError(error, context = {}) {
        TaskSystemLogger.error('TASK_SYSTEM_ERROR', {
            message: error.message,
            stack: error.stack,
            ...context
        });
    }
};

// Export default setup function
export default setupTaskSystemLogging;